import axios from "axios";
import i18n from "i18next";
import type { AxiosError } from "axios";
import type { ApiErrorBody } from "@/types";

const API_ERROR_MESSAGE_FIELDS = [
  "message",
  "detail",
  "description",
  "error_description",
  "error",
] as const;

type ApiErrorMessageField = (typeof API_ERROR_MESSAGE_FIELDS)[number];

const AXIOS_CANCELED_ERROR_CODE = "ERR_CANCELED";
const OAUTH_INVALID_CLIENT_ERROR = "invalid_client";
const OTP_CHALLENGE_MESSAGE_RE = /\b(?:otp|totp|one[-\s]?time|2fa|mfa)\b/i;

interface OtpChallengeDetectionOptions {
  allowInvalidClientFallback?: boolean;
}

class OtpRequiredError extends Error {
  name = "OtpRequiredError";
}

function normalizeMessage(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const message = value.trim();
  return message || null;
}

function extractApiResponseErrorMessage(
  data: ApiErrorBody | string | undefined,
): string | null {
  const directMessage = normalizeMessage(data);
  if (directMessage) return directMessage;
  if (!data || typeof data !== "object") return null;

  const errorBody = data as Partial<Record<ApiErrorMessageField, unknown>>;
  for (const field of API_ERROR_MESSAGE_FIELDS) {
    const fieldMessage = normalizeMessage(errorBody[field]);
    if (fieldMessage) return fieldMessage;
  }

  return null;
}

function extractBodyMessages(data: ApiErrorBody | string | undefined): string[] {
  const directMessage = normalizeMessage(data);
  if (directMessage) return [directMessage];
  if (!data || typeof data !== "object") return [];

  const errorBody = data as Partial<Record<ApiErrorMessageField, unknown>>;
  return API_ERROR_MESSAGE_FIELDS
    .map((field) => normalizeMessage(errorBody[field]))
    .filter((message): message is string => Boolean(message));
}

export function extractErrorMessage(err: unknown, fallback = ""): string {
  if (axios.isAxiosError(err)) {
    const axErr = err as AxiosError<ApiErrorBody | string>;
    if (axErr.response?.status === 401
      && (axErr.response.data as ApiErrorBody | undefined)?.error === "invalid_token") {
      return i18n.t("common.sessionExpired");
    }
    if (axErr.response?.status === 502) return i18n.t("common.serviceUnavailable");
    if (extractTypedApiError(err, "InsufficientTariffBalanceError")) {
      return i18n.t("common.insufficientTariffBalance");
    }
    const bodyMessage = extractApiResponseErrorMessage(axErr.response?.data);
    if (bodyMessage) return bodyMessage;
    if (axErr.response) return fallback || normalizeMessage(axErr.message) || "";

    return (
      normalizeMessage(axErr.message)
      ?? fallback
    );
  }
  if (err instanceof Error) return normalizeMessage(err.message) ?? fallback;
  return fallback;
}

export function extractTypedApiError(
  error: unknown,
  type: unknown,
): Record<string, unknown> | null {
  if (typeof type !== "string" || !axios.isAxiosError(error)) return null;
  const data: unknown = error.response?.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const body = data as Record<string, unknown>;
  return body.type === type ? body : null;
}

export function extractPublicErrorMessage(err: unknown, fallback = ""): string {
  if (err instanceof OtpRequiredError) {
    return normalizeMessage(err.message) ?? fallback;
  }

  if (!axios.isAxiosError(err)) return fallback;

  const axErr = err as AxiosError<ApiErrorBody | string>;
  if (axErr.response?.status === 401
    && (axErr.response.data as ApiErrorBody | undefined)?.error === "invalid_token") {
    return i18n.t("common.sessionExpired");
  }
  if (axErr.response?.status === 502) return i18n.t("common.serviceUnavailable");
  const bodyMessage = extractApiResponseErrorMessage(axErr.response?.data);
  if (bodyMessage) return bodyMessage;

  return fallback || normalizeMessage(axErr.message) || "";
}

export function isRequestCanceled(err: unknown): boolean {
  return (
    axios.isCancel(err)
    || (axios.isAxiosError(err) && err.code === AXIOS_CANCELED_ERROR_CODE)
  );
}

export function isOtpChallengeResponse(
  err: unknown,
  { allowInvalidClientFallback = false }: OtpChallengeDetectionOptions = {},
): boolean {
  if (!axios.isAxiosError(err) || err.response?.status !== 401) {
    return false;
  }

  const messages = extractBodyMessages(err.response.data as ApiErrorBody | string);
  if (messages.some((message) => OTP_CHALLENGE_MESSAGE_RE.test(message))) {
    return true;
  }

  return (
    allowInvalidClientFallback
    && messages.length === 1
    && messages[0] === OAUTH_INVALID_CLIENT_ERROR
  );
}

export function createOtpRequiredError(err: unknown, fallback: string) {
  return new OtpRequiredError(extractErrorMessage(err, fallback) || fallback);
}

export function isOtpRequiredError(err: unknown): err is OtpRequiredError {
  return err instanceof OtpRequiredError;
}
